import { EntityMainNameDefault, showConfirmationDialog, showToast } from "./utils.jsx";
import { IMPORT_TYPES_HISTORY, NO_VALUE } from "../constants/OtherKeys.jsx";
import { DateHelper, EventHelper } from "@bryntum/schedulerpro";
import { sendNudgeAlert } from "../../network/network.jsx";
import { urlValidation } from "../../bryntum-lib/widget/fields/SwivuUrlField.jsx";
import { STORE_KEYS } from "../constants/StoreKeys.jsx";

/**
 * @param {object[]} columns
 * @param {object} project
 * @param {object?} otherParams
 * @description - class the onLoad function on columns that extends ColumnBase class
 */
export const callOnLoadOnColumns = (columns, project, otherParams) => {
  columns.forEach((col) => col.isColumnBase && col.onLoad(project, otherParams));
};

/**
 * @description - this is a helper function for editing grid specific row, specific cell
 * @param grid - bryntum grid instance
 * @param {{ field: string, id: string }} cellContext - Provide detail for which row and which field cell to edit, default will be to select last row second cell
 */

export const swivuSortAscendingKey = "swivuSortAscending";

export const canOpenEditor = (column, record, fields = null) => !record.isSpecialRow && (Array.isArray(fields) ? fields.includes(column.field) : (!column || !column.editor));

/**
 * @param {Object[]} widgets - any widget that has "state" and "stateProvider"
 * @param viewStateProvider - state provider that each widget will use for saving / restoring the widget's state
 */
export const assignViewsState = (widgets = [], viewStateProvider) => {
  if (viewStateProvider) {
    widgets.forEach((vs) => {
      vs.state = viewStateProvider.getValue(vs.stateId);
      // where an applied filter returns an empty dataset in grid and comeback to the same view the list remain empty, so this will let grid know to refresh grid
      if (vs.store) { // checkin store since nested grid don't have store instance by default
        vs.store.refreshData();
      }
      vs.stateProvider = viewStateProvider;
    });
  }
};

/**
 * @param {number} count - on count basses the noun will be pluralized
 * @param {string} noun - word on which to decide to append suffix or not
 * @param {string} suffix - default to "s"
 * @return {string} - e.g, count is 4, noun = "record", result => "records"
 */

export const setUserFromResponse = (resp, onSuccess) => {
  resp.then((res) => {
    if (res?.data?.id) {
      onSuccess(res.data);
    } else showToast({ msg: "Invalid server response.", type: "error" });
  }).catch((err) => {
    onSuccess(null);
    let errorMessageDetails = err?.response?.data?.details || err?.response?.data;
    if (typeof  errorMessageDetails === "object") {
      errorMessageDetails = Object.values(errorMessageDetails).flat()[0];
    }
    showToast({
      msg: errorMessageDetails || "We're having trouble connecting.  Please try again.",
      type: "error"
    });
  });
};
export const pluralize = (count, noun, suffix = 's') => `${noun}${count > 1 ? suffix : ''}`;

/**
 * @param {string} field
 * @param {string} storeName
 */
export const customGroupByOrder = (field, storeName) => {
  return {
    text: "Group by Settings",
    icon: "fa fa-signal",
    cls: "group-by-setting",
    weight: 420,
    isDisabled: true,
    onItem: ({ source, column }) => {
      const { store } = source;

      store.group({
        field: column.field,
        fn: STORE_GROUPERS.customOrderGrouper(field, storeName)
      });
    }
  }
}

export const STORE_GROUPERS = Object.freeze({
  customOrderGrouper: (field, storeName) => {
    function customOrderGrouper(groupA, groupB) {
      const [recordA] = groupA.groupChildren || [];
      const [recordB] = groupB.groupChildren || [];

      // First perform check that will preserve records' positions
      // Groupers should return `null` to allow other sorters to have a shot
      if (recordA?.[field] === recordB?.[field]) return null;
      else if (!recordA?.[field] || recordA?.[field] === NO_VALUE) return 1;
      else if (!recordB?.[field] || recordB?.[field] === NO_VALUE) return -1;

      const storeRecordA = recordA[storeName].getById(recordA[field]);
      const storeRecordB = recordB[storeName].getById(recordB[field]);

      if (storeRecordA && storeRecordB) {
        return (storeRecordA.order - storeRecordB.order) || null;
      }
      else {
        return null;
      }
    }

    customOrderGrouper.fnName = "customOrderGrouper"
    // This key allows us to tell grouper types apart
    customOrderGrouper.stateful = true;
    // Args are required for function generator. These args are serialized
    customOrderGrouper.args = [field, storeName];

    return customOrderGrouper;
  }
});

export const getIsArchivedForBoard = (boardId) => boardId && (JSON.parse(localStorage.getItem("archived") || "{}"))[boardId];

export const saveIsArchivedForBoard = (boardId, isArchived) => {
  const existingArchivedRecord = JSON.parse(localStorage.getItem("archived") || "{}");
  localStorage.setItem("archived", JSON.stringify({ ...existingArchivedRecord, [boardId]: isArchived }));
}

export const setClientFieldsDisabledV2 = (widgetMap, isDisable) => {
  const {
    clientWebsiteLinkField,
    clientAddressField,
    clientStatusField,
    clientCategoryField,
    clientTagsField,
    clientTeamField,

  } = widgetMap;

  [
    clientWebsiteLinkField,
    clientAddressField,
    clientStatusField,
    clientCategoryField,
    clientTagsField,
    clientTeamField,

  ].forEach((field) => {
    if (field) field.disabled = isDisable;
  });
};

export const debounce = (cb, delay = 1000) => {
  let timer;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      cb(...args);
    }, delay);
  }
}

/*Since the wheel event is only being attached when the scheduler is constructed,
therefore we are adding the event listener here based on userPreferences*/
// Todo: Consider renaming the 'addTimelineZoom' function to better indicate its purpose of attaching a mouse wheel event listener to the timeline like `attachMouseWheelZoomToTimeline`.
export const addTimelineZoom = (scheduler) => (
  EventHelper.on({
    element: scheduler.timeAxisSubGridElement,
    wheel: 'onWheel',
    // Throttle zooming with the wheel a bit to have greater control of it
    throttled: {
      buffer: 100,
      // Prevent events from slipping through the throttle, causing scroll
      alt: e => e.ctrlKey && e.preventDefault()
    },
    thisObj : scheduler,
    capture : true,
    passive : false
  })
)

export const getDefaultFormatShareMessage = ({ schedulerLink, entity }) => {
  if (schedulerLink && entity) {
    return `${entity.name || "N/A"}\n${schedulerLink}`;
  }
  return "";
}

export const htmlEncode = (str) =>
  String(str).replace(/[^\w. ]/gi, (c) => `&#${c.charCodeAt(0)};`);

export const isValidUrl = (urlString) => {
  return !!urlValidation.test(urlString);
};

export const createTaskEvent = ({schedule, task, date, resource, eventStartDate, eventEndDate}) => {
  // Suspending refresh to not have multiple redraws from date change and assignments (will animate weirdly)
  schedule.suspendRefresh();

  const startDate = eventStartDate || date;
  const endDate = eventEndDate || DateHelper.add(date, 1, 'day');

  // Create new event and link it to the task
  const [newEvent] = schedule.eventStore.add({taskId: task.id, startDate, endDate, archived: task.archived});

  newEvent.assign(resource);

  // No longer suspending refresh, all operations have finished
  schedule.resumeRefresh();

  // Redraw, wihout transitions to not delay dependency drawing
  schedule.refresh();

  return newEvent;
}

export const tagWidget = (text) => EntityMainNameDefault({ text });
export const tagWidgetForEach = (tags) => (
  (tags && tags.length) ? tags.map((tag) => tagWidget(tag.tagValue)) : tagWidget()
);

export const getEntityNames = (contacts = [], entityStoreName) => {
  let entityRecordKey = null;
  let entityRelationKey = null;
  let entityContactStoreKey = null;
  let entityNamePrefix = "";
  if (entityStoreName === STORE_KEYS.taskStore) {
    entityRecordKey = "taskRecord";
    entityContactStoreKey = STORE_KEYS.taskContactStore
    entityRelationKey = "taskId"
  }
  else if (entityStoreName === STORE_KEYS.resourceStore) {
    entityRecordKey = "resourceRecord";
    entityContactStoreKey = STORE_KEYS.resourceContactStore;
    entityRelationKey = "resourceId";
  }
  else if (entityStoreName === STORE_KEYS.clientStore) {
    entityRecordKey = "clientRecord";
    entityContactStoreKey = STORE_KEYS.clientContactStore;
    entityRelationKey = "clientId";
  }
  else if (entityStoreName === STORE_KEYS.boardUserStore) {
    entityRecordKey = "userRecord";
    entityContactStoreKey =  STORE_KEYS.userContactStore;
    entityRelationKey = "userId";
    entityNamePrefix = "Board User: ";
  }
  return contacts.map((entityContact) => {
    const entityRecord = entityContact[entityRecordKey];
    const entityType = entityRecord.entityVisibleName;
    return {
      id: `${entityType}-${entityRecord.id}`,
      name: entityNamePrefix + entityRecord.name,
      type: entityType,
      entityRecord,
      entityContactStoreKey,
      entityRelationKey
    }
  })
}

export const getScheduleInstanceFromViewManager = (viewManager) => {
  const pageViewsRef = viewManager.pageViewsRef;
  if(pageViewsRef) {
    return Object.entries(pageViewsRef).find(([, pageRef]) => pageRef.isSchedulerPro)?.[1];
  }
}

// Regions are named based on the subgrid config in grid
// For example: subGridConfig: {primary: {weight: 20,width: 225}} then item in headerMenu would be "primaryRegion"
export const gridHeaderMenuProcessItems = (leftRegion, rightRegion) => ({ items, column }) => {
  const pinItem = items[leftRegion];
  const unPinItem = items[rightRegion];
  if (unPinItem) {
    unPinItem.text = "Unpin";
    unPinItem.icon = "fa-unlock";
    unPinItem.hidden = unPinItem.disabled;
    const unPinnedHandler  = unPinItem.onItem;
    unPinItem.onItem = (e) => {
      showConfirmationDialog({
        title: "Unpinning will move the column",
        confirmBtnText: "Unpin Column",
        message: `When you unpin a column, the column is moved to the END of the adjacent grid. <br/> To arrange the order of columns, you can drag and drop them.`,
      }).then((isUnpin) => {
        if (isUnpin) {
          unPinnedHandler(e);
        }
      });
    }
  }
  if (pinItem) {
    pinItem.text = "Pin";
    pinItem.icon = "fa-lock";
    pinItem.hidden = pinItem.disabled;
  }
  if (items.textWrapping) {
    items.textWrapping.hidden = items.textWrapping.regionsToIgnore.includes(column.region);
    items.textWrapping.checked = column.autoHeight;
    items.textWrapping.onItem = () => {
      column.autoHeight = !column.autoHeight;
    }
  }
}

export const triggerNudgeAlert = async (checklistId, project, widget) => {
  try {
    widget?.mask("Loading...");
    const {data: {reminder, reminder_recipients, nudge_date}} = await sendNudgeAlert(checklistId);
    project.applyProjectChanges({
      [STORE_KEYS.reminderStore]: { added: [reminder] },
      [STORE_KEYS.reminderRecipientStore]: { added: reminder_recipients },
      [STORE_KEYS.checklistStore]: { updated: [{ id: checklistId, nudge_date }]}
    })
    showToast({ msg: "Nudge Sent", type: "success" });
  } catch (error) {
    showToast({ msg: error.message ?? "Something Went Wrong", type: "error" })
  } finally {
    widget?.unmask();
  }
}
export const createTooltipRow = (
  value,
  className,
  label,
  isValueArray = false,
  valueStyle = "",
  labelStyle = ""
) => {
  let fieldValue = value;
  if (isValueArray) fieldValue = value.length ? value.join(", ") : "";
  return fieldValue ? `<div class="${className}">
    ${label ? `
      <span style="${labelStyle}" class="field-label">${label}</span>
      <span style="${valueStyle}" class="field-value">${fieldValue}</span>
    ` : `<span style="${valueStyle}">${fieldValue}</span>`}
  </div>` : "";
};

export const onTaskGridColumnDrop = (regionsToIgnore) => ({ valid, column, region }) => {
  if (valid && regionsToIgnore.includes(region)) {
    column.autoHeight = false;
  }
}

export const IMPORT_TYPE_NAMES = Object.fromEntries(
  Object.entries(IMPORT_TYPES_HISTORY).map(([key, value]) => [
    value,
    key.charAt(0).toUpperCase() + key.slice(1)
  ])
);
